import {
  Archive,
  Close,
  Download,
  Edit,
  NavigateNext,
  NotificationAddOutlined,
  NotificationsOffRounded,
  Send,
} from "@mui/icons-material";
import {
  Link,
  Box,
  Breadcrumbs,
  Button,
  ButtonGroup,
  Container,
  Fab,
  Grid,
  LinearProgress,
  MenuItem,
  Paper,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  useTheme,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select
} from "@mui/material";
import PromoItemsService from "modules/PromoItems/services/promoItems.service";
import { useMutation, useQuery } from "@tanstack/react-query";
import "chart.js/auto";
import { MaterialReactTable } from "material-react-table";
import TextFieldWrapper from "modules/Forms/components/textFIeldWrapper.component";
import { Slide } from "modules/Layout/views/landing.view";
import CampaignActivityLog from "modules/UI/components/ActivityLog.component";
import { Attachments } from "modules/UI/components/Attachments.component";
import Drawer from "modules/UI/components/Drawer.component";
import PUIRatioBox from "modules/UI/components/RatioBox.component";
import PUIUser from "modules/UI/components/User.component";
import { useJustification } from "modules/UI/hooks/useJustification.hook";
import { useActiveUser } from "modules/User/activeUser";
import { IUserRole } from "modules/User/service/user";
import moment from "moment";
import React, { useEffect, useRef } from "react";
import { NavLink, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { createBlobService } from "utils/factories";
import saveAs from "file-saver";
import * as yup from "yup";
import { CampaignMaterialCard } from "../components/CampaignMaterialCard.component";
import CampaignMaterialForm from "../components/CampaignMaterialForm.component";
import CampaignMaterialPreOrderButton from "../components/CampaignMaterialPreOrderButton.component";
import CampaignsService from "../service/campaigns.service";
import {
  CampaignMaterialType,
  CampaignState,
  MediaFileDTO,
} from "../service/campaigns.types";
import CreateCampaignContainer from "./create.container";
import { SubscribersContainer } from "./subscribers.container";
import { CustomCurrencySelect } from "modules/PromoItems/components/PromoItemRow.component";
import { useUserPermissions } from "modules/User/service/userPermissions.hook";
import AdminService from "modules/Admin/service/admin.service";


const isPendingState = (state: CampaignState): boolean => {
  return (
    state === CampaignState.Pending_NPD ||
    state === CampaignState.Pending_GlobalQuality ||
    state === CampaignState.Pending_Rejected_NPD ||
    state === CampaignState.Pending_Market_Proc ||
    state === CampaignState.Pending_Rejected_GlobalQuality ||
    state === CampaignState.Pending_Confirm_Price
  );
};
const canCreateOrDuplicateMaterial = (state: CampaignState): boolean => {
  return (
    state === CampaignState.Draft ||
    state === CampaignState.Pending_NPD
  );
};

const canEditMaterials = (state: CampaignState): boolean => {
  return (
    state === CampaignState.Draft ||
    (isPendingState(state) &&
      state !== CampaignState.Pending_Confirm_Price &&
      state !== CampaignState.Pending_Market_Proc)
  );
};

const canEditCampaign = (state: CampaignState): boolean => {
  return (
    state === CampaignState.Draft ||
    state === CampaignState.Active ||
    isPendingState(state)
  );
};

const canSubmitPendingCampaign = (state: CampaignState): boolean => {
  return (
    isPendingState(state) &&
    state !== CampaignState.Pending_Confirm_Price &&
    state !== CampaignState.Pending_Market_Proc
  );
};

const canPreOrderCampaign = (state: CampaignState): boolean => {
  return (
    state === CampaignState.Active ||
    state === CampaignState.Pending_Market_Proc
  );
};

const getStateDisplayLabel = (state: CampaignState): string => {
  switch (state) {
    case CampaignState.Draft:
      return "Draft";
    case CampaignState.Active:
      return "Active";
    case CampaignState.Past:
      return "Closed";
    case CampaignState.Pending_NPD:
      return "Pending NPD";
    case CampaignState.Pending_GlobalQuality:
      return "Pending - Global Quality";
    case CampaignState.Pending_Rejected_NPD:
      return "Pending - Rejected NPD";
    case CampaignState.Pending_Market_Proc:
      return "Pending - Market Proc";
    case CampaignState.Pending_Rejected_GlobalQuality:
      return "Pending - Rejected Global Quality";
    case CampaignState.Pending_Confirm_Price:
      return "Pending - Confirm Price";
    case CampaignState.Closed_Submit_PR:
      return "Closed - Submit PR";
    case CampaignState.Archived:
      return "Archived";
    default:
      return String(state);
  }
};

type VendorOption = {
  vendorCode: string;
  vendorName: string;
};

const normalizeVendorCode = (value: unknown) =>
  value == null ? "" : String(value).trim();

const normalizeVendorName = (value: unknown) =>
  value == null ? "" : String(value).trim();

const normalizeVendorList = (vendorList: any[] = []): VendorOption[] =>
  vendorList.map((vendor) => ({
    vendorCode: normalizeVendorCode(vendor.vendorCode),
    vendorName: normalizeVendorName(vendor.vendorName),
  }));

const getRowVendor = (rowData: any): VendorOption | null => {
  const vendorCode = normalizeVendorCode(
    rowData.vendorCode ?? rowData.vendor?.vendorCode
  );
  const vendorName = normalizeVendorName(
    rowData.vendorName ?? rowData.vendor?.vendorName
  );

  if (!vendorCode && !vendorName) return null;

  return {
    vendorCode,
    vendorName,
  };
};

const getSelectedVendorForRow = (
  vendorList: VendorOption[],
  rowData: any
): VendorOption | null => {
  const rowVendor = getRowVendor(rowData);

  if (!rowVendor) return vendorList[0] ?? null;

  const vendorByCodeAndName = vendorList.find(
    (vendor) =>
      vendor.vendorCode === rowVendor.vendorCode &&
      (!rowVendor.vendorName || vendor.vendorName === rowVendor.vendorName)
  );

  if (vendorByCodeAndName) return vendorByCodeAndName;

  const vendorByCode = vendorList.find(
    (vendor) => vendor.vendorCode === rowVendor.vendorCode
  );

  if (vendorByCode) return vendorByCode;

  return rowVendor.vendorCode ? rowVendor : vendorList[0] ?? null;
};

const getVendorsWithSelectedVendor = (
  vendorList: VendorOption[],
  selectedVendor: VendorOption | null
): VendorOption[] => {
  if (
    !selectedVendor?.vendorCode ||
    vendorList.some((vendor) => vendor.vendorCode === selectedVendor.vendorCode)
  ) {
    return vendorList;
  }

  return [selectedVendor, ...vendorList];
};

export const CampaignDetailsContainer = () => {

  // const [adjustedPrice, setAdjustedPrice] = React.useState<number | string>("-");
  const [adjustedPrice, setAdjustedPrice] =
    React.useState<number | "">("");
  const [comment, setComment] = React.useState("");
  const [fob, setFob] = React.useState("");
  const currentUser = useActiveUser();
  const { data: userPermissions } = useUserPermissions();
  const roleId = userPermissions?.roleId;

  const isSupplier = roleId === IUserRole.SUPPLIER;

  const isRequestor = roleId === IUserRole.REQUESTOR;

  const hasPermission = (permission: string) =>
    userPermissions?.permissions?.some(
      (p) => p.name === permission
    ) ?? false;

  // Material permissions from BE

  const canCreateMaterial = hasPermission("Campaign.Edit");

  const canUpdateMaterial = hasPermission("Campaign.Edit");

  const canDuplicateMaterial = hasPermission("Campaign.Edit");

  const canDeleteMaterial = hasPermission("Campaign.Edit");

  const navigate = useNavigate();
  const [materialQuantities, setMaterialQuantities] = React.useState<Record<number, number>>({});
  const [activeSlide, setActiveSlide] = React.useState(0);
  const [createDialogOpen, setCreateDialogOpen] = React.useState(false);
  const [images, setImages] = React.useState<MediaFileDTO[]>([]);
  const { campaignId } = useParams();
  const [tab, setTab] = React.useState(0);
  const [adjustModalOpen, setAdjustModalOpen] = React.useState(false);
  const [adjustModalData, setAdjustModalData] = React.useState<any>(null);
  const theme = useTheme();
  const [drawerInitialData, setDrawerInitialData] =
    React.useState<CampaignMaterialType>(null);
  const [quantity, setQuantity] = React.useState(0);
  const [currency, setCurrency] = React.useState("");
  const [initialMarketProcAdjustValues, setInitialMarketProcAdjustValues] =
    React.useState({ incoTermId: 0, quantity: 0, comment: "" });
  const [materialName, setMaterialName] = React.useState("");
  const [isRequestorEditMode, setIsRequestorEditMode] = React.useState(false);
  const [vendors, setVendors] = React.useState<VendorOption[]>([]);

  const [selectedVendor, setSelectedVendor] =
    React.useState<VendorOption | null>(null);

  const {
    data: { data: materials },
    refetch: refetchMaterials,
  } = useQuery({
    queryKey: ["materials", campaignId],
    queryFn: async () => {
      const response = await CampaignsService.getCampaignMaterials({
        orderWindowId: Number(campaignId),
        take: 100,
        skip: 0,
      });

      setMaterialQuantities(
        response.data.reduce((acc, material) => {
          acc[material.id] = material.minimumOrderQuantity;
          return acc;
        }, {})
      );

      return response;
    },

    initialData: {
      data: [],
      take: 100,
      skip: 0,
      total: 0,
    },
  });

  const { data: subscribers, refetch: refetchSubscribers } = useQuery({
    queryKey: ["subscribers", campaignId],
    queryFn: () => CampaignsService.getCampaignSubscribers({ campaignId }),
    initialData: [],
  });
  const {
    data: tasksData,
    isLoading: tasksLoading,
    isError,
  } = useQuery({
    queryKey: ["tasks", campaignId],
    queryFn: async () => {
      const res = await CampaignsService.getTasksByOrderWindowId({
        orderWindowId: Number(campaignId),
        take: 10,
        skip: 0,
      });
      console.log("API response:", res);
      return res;
    },
    enabled: !!campaignId,
    retry: false,
  });
  console.log("tasksData:", tasksData);
  const { data: activityLog, refetch: refetchActivityLog } = useQuery({
    queryKey: ["activityLog", campaignId],
    queryFn: () => CampaignsService.getCampaignActivityLog({ campaignId }),
    initialData: [],
  });


  const { data: materialActivityLog, refetch: refetchMaterialActivityLog } =
    useQuery({
      queryKey: ["materialActivityLog", campaignId],
      queryFn: () =>
        CampaignsService.getCampaignMaterialsActivityLog({ campaignId }),
      initialData: [],
    });

  const combinedActivityLogs = React.useMemo(() => {
    return [...activityLog, ...materialActivityLog];
  }, [activityLog, materialActivityLog]);

  const {
    data: details,
    refetch: refetchDetails,
    isLoading: loadingDetails,
  } = useQuery({
    queryKey: ["campaigns/details", campaignId],
    queryFn: async () => {
      const response = await CampaignsService.getCampaign({
        id: campaignId,
      });
      return response;
    },
    initialData: null,
  });

  const spendCategoryCode = details?.spendCategoryL3?.code;
  const { data: pslSpendCats = [] } = useQuery({
    queryKey: ["psl-spend-cat"],

    queryFn: async () => {
      return await AdminService.getPslSpendCat();
    },

    initialData: [],
  });
  useEffect(() => {
    console.log("spendCategoryCode =", spendCategoryCode);

    console.log("pslSpendCats =", pslSpendCats);

    if (pslSpendCats?.length) {
      console.log("first item =", pslSpendCats[0]);
    }
  }, [pslSpendCats, spendCategoryCode]);

  const matchedSpendCat = React.useMemo(() => {
    if (!pslSpendCats?.length || !spendCategoryCode) return null;

    for (const level1 of pslSpendCats) {
      for (const level2 of level1.spendCategories || []) {
        const level3 = (level2.spendCategories || []).find(
          (item: any) => item.code === spendCategoryCode
        );

        if (level3) {
          return level3;
        }
      }
    }

    return null;
  }, [pslSpendCats, spendCategoryCode]);

  console.log("spendCategoryCode outside query =", spendCategoryCode);
  const { data: defaultMaterials } = useQuery({
    queryKey: ["campaign-default-material", spendCategoryCode],
    enabled: !!spendCategoryCode && !!matchedSpendCat,
    queryFn: async () => {
      console.log("spendCategoryCode =", spendCategoryCode);

      const payload = {
        materialgroup: spendCategoryCode,
        //searchTerm: "000",
        searchTerm: matchedSpendCat?.materialCode || "",
        //locationCompanyCodes: "CL10,CL11,CL23,CL26",
      };

      console.log("API Payload =", payload);

      const response = await PromoItemsService.getPslAmdrMaterials(payload);

      console.log("API Response =", response);

      return response;
    },
  });
  const { data: promoCategories = {} as Record<string, any> } = useQuery({

    queryKey: ["promo-categories/get-all-hash"],
    queryFn: async () => {
      const response: any[] = await AdminService.getAllPromoCategories();

      return response.reduce((acc: any, curr: any) => {
        acc[curr.id] = curr;
        return acc;
      }, {});
    },
  });
  console.log("promoCategories", promoCategories);
  console.log("promoCategory keys", Object.keys(promoCategories));



  console.log("pslSpendCats", pslSpendCats);



  console.log("matchedSpendCat", matchedSpendCat);

  useEffect(() => {
    console.log("defaultMaterials", defaultMaterials);

    if (defaultMaterials?.length > 0) {
      console.log("first material", defaultMaterials[0]);
    }
  }, [defaultMaterials]);

  console.log("details", JSON.stringify(details, null, 2));
  const {
    data: requests,
    refetch: refetchRequests,
    isLoading: loadingRequests,
  } = useQuery({
    queryKey: ["campaigns/requests", campaignId],
    queryFn: async () => {
      const response = await CampaignsService.getQuantityRequests({
        id: campaignId,
      });
      return response;
    },
    initialData: [],
  });

  const { mutateAsync: publishCampaign, isPending: publishingCampaign } =
    useMutation({
      mutationKey: ["publishCampaign", campaignId],
      mutationFn: async () => {
        await CampaignsService.publishCampaign({ campaignId });
      },
      onSuccess: () => {
        toast.success("Campaign published successfully!");
        navigate("/campaigns");
      },
    });

  const { mutateAsync: closeCampaign, isPending: closingCampaign } =
    useMutation({
      mutationKey: ["closingCampaign", campaignId],
      mutationFn: async () => {
        await CampaignsService.closeCampaign({ campaignId });
      },
      onSuccess: () => {
        toast.success("Order window was successfully closed!");
        navigate("/campaigns");
      },
    });

  const handleAdjustSubmit = async () => {
    if (!adjustModalData) return;

    try {
      // const payload = {
      //   id: adjustModalData.id,
      //   quantity: Number(quantity),
      //   incoTermId: adjustModalData.incoTerm?.id ?? 0,
      //   comments: comment ?? "",
      //   quantityUnits: adjustModalData.quantityUnits ?? "",
      //   materialName: materialName,
      //   // adjustedPrice: Number(adjustedPrice),
      //   adjustedPrice:
      //     adjustedPrice === ""
      //       ? 0
      //       : Number(adjustedPrice),
      //   //currencyCode: currency || adjustModalData?.material?.material?.currencyCode || "USD",
      //   adjustedCurrencyCode: currency,
      //   vendorCode: selectedVendor?.vendorCode || "",
      //   vendorName: selectedVendor?.vendorName || "",
      // };
const payload = {
  id: adjustModalData.id,
  quantity: Number(quantity),
  incoTermId: adjustModalData.incoTerm?.id ?? 0,
  comments: comment ?? "",
  quantityUnits: adjustModalData.quantityUnits ?? "",
  materialName,
  adjustedPrice:
    adjustedPrice === ""
      ? 0
      : Number(adjustedPrice),

  currencyCode: currency,   // <-- send CurrencyCode instead

  vendorCode: selectedVendor?.vendorCode || "",
  vendorName: selectedVendor?.vendorName || "",
};
      //  Use mutation instead of direct API call
      await adjustRequest(payload);

      toast.success("Request updated successfully!");

      setAdjustModalOpen(false);

    } catch (error) {
      console.error(error);
      toast.error("Failed to update request");
    }
  };
  useEffect(() => {
    if (!details || details?.mediaFiles?.length === 0) return;
    details?.mediaFiles
      ?.filter((file) => file.mimeType.indexOf("image/") > -1)
      .forEach((image) => {
        createBlobService(image.url, "GET")().then((blob) => {
          setImages((prev) => [
            ...prev,
            { ...image, url: URL.createObjectURL(blob) },
          ]);
        });
      });
  }, [details]);

  const {
    mutateAsync: unsubscribeMemberFromCampaign,
    isPending: unsubscribePending,
  } = useMutation({
    mutationKey: ["unsubscribe", campaignId],
    mutationFn: (email: string) =>
      CampaignsService.unsubscribeMemberFromCampaign({
        campaignId: campaignId,
        email,
      }),
    onSuccess: (data, variables, context) => {
      toast.success("Unsubscribed successfully");
      refetchSubscribers();
    },
  });
  const { mutate: subcribeMeToCampaign, isPending: subscribeMePending } =
    useMutation({
      mutationKey: ["subscribeMe", campaignId],
      mutationFn: () => CampaignsService.subcribeMeToCampaign({ campaignId }),
      onSuccess: (data, variables, context) => {
        toast.success(
          "You are now subscribed for updates about this campaign."
        );
        refetchSubscribers();
      },
    });

  const {
    mutateAsync: removeCampaignMaterial,
    isPending: removeCampaignMaterialPending,
  } = useMutation({
    mutationKey: ["removeCampaignMaterial"],
    mutationFn: (materialId: number) =>
      CampaignsService.deleteCampaignMaterial({ id: materialId }),
    onSuccess: (data, variables, context) => {
      toast.success("Material removed successfully");
      refetchMaterials();
      refetchDetails();
      refetchMaterialActivityLog();
    },
  });

  const {
    mutateAsync: duplicateCampaignMaterial,
    isPending: duplicateCampaignMaterialPending,
  } = useMutation({
    mutationKey: ["duplicateCampaignMaterial"],
    mutationFn: (materialId: number) =>
      CampaignsService.duplicateCampaignMaterial({ materialId }),
    onSuccess: (data, variables, context) => {
      toast.success("Material duplicated successfully");
      refetchMaterials();
      refetchDetails();
      refetchMaterialActivityLog();
    },
  });

  const { mutateAsync: uploadCampaignMedia } = useMutation<
    any,
    any,
    Parameters<typeof CampaignsService.uploadCampaignMedia>[0]
  >({
    mutationKey: ["uploadCampaignMedia"],
    mutationFn: async (data) => {
      const response = await CampaignsService.uploadCampaignMedia(data);
      return response;
    },
  });

  const { mutateAsync: deleteCampaignMedia } = useMutation<
    any,
    any,
    Parameters<typeof CampaignsService.deleteCampaignMedia>[0]
  >({
    mutationKey: ["deleteCampaignMedia"],
    mutationFn: async ({ fileId }) => {
      await CampaignsService.deleteCampaignMedia({ fileId });
      return fileId;
    },
    onSuccess: () => {
      toast.success("File deleted successfully!");
      refetchDetails();
    },
  });
  const { mutateAsync: deleteQuantityRequest } = useMutation<
    any,
    any,
    Parameters<typeof CampaignsService.deleteQuantityRequest>[0]
  >({
    mutationKey: ["deleteQuantityRequest"],
    mutationFn: async ({ id }) => {
      await CampaignsService.deleteQuantityRequest({ id });
      return id;
    },
    onSuccess: () => {
      toast.success("Request revoked successfully!");
      refetchRequests();
      refetchMaterialActivityLog();
    },
  });

  const { mutate: exportQuantityConsolidation } = useMutation({
    mutationKey: ["exportQuantityConsolidation", campaignId],
    mutationFn: async () => {
      const response = await CampaignsService.exportQuantityConsolidation({
        id: campaignId,
      });
      saveAs(response, "quantity-consolidation.xlsx");
    },
    onSuccess: () => {
      toast.success("Export completed successfully!");
    },
    onError: () => {
      toast.error("Export failed. Please try again.");
    },
  });




  const uomList = ["EA"];

  const { mutateAsync: archiveCampaign, isPending: archivingCampaign } =
    useMutation<
      any,
      any,
      Parameters<typeof CampaignsService.archiveCampaign>[0]
    >({
      mutationKey: ["archiveCampaign"],
      mutationFn: async ({ campaignId }) => {
        await CampaignsService.archiveCampaign({ campaignId });
        return campaignId;
      },
      onSuccess: () => {
        toast.success("Campaign archived successfully!");
        refetchDetails();
      },
    });


  //   const { mutateAsync: adjustRequest } = useMutation({
  //     mutationFn: CampaignsService.adjustQuantityRequest,

  //     onSuccess: (_, variables) => {
  //       toast.success("Adjusted successfully");

  //       setAdjustModalData((prev: any) => ({
  //   ...prev,
  //   adjustedCurrencyCode: variables?.adjustedCurrencyCode,
  // }));

  //       // ✅ refresh table
  //       refetchRequests();
  //     },
  //   });

  const { mutateAsync: adjustRequest } = useMutation({
    mutationFn: CampaignsService.adjustQuantityRequest,

    onSuccess: () => {
      toast.success("Adjusted successfully");
      refetchRequests();
    },
  });

  const { mutateAsync: duplicateCampaign, isPending: duplicatingCampaign } =
    useMutation<
      any,
      any,
      Parameters<typeof CampaignsService.duplicateCampaign>[0]
    >({
      mutationKey: ["duplicateCampaign"],
      mutationFn: async ({ campaignId }) => {
        const res = await CampaignsService.duplicateCampaign({ campaignId });
        return res;
      },
      onSuccess: (res) => {
        toast.success(`New Order Window created: ${res?.idWithPrefix}`);
        refetchDetails(); // optional
      },
    });
  const { mutateAsync: confirmPriceCampaign, isPending: confirmingPrice } =
    useMutation<
      any,
      any,
      Parameters<typeof CampaignsService.confirmPrice>[0]
    >({
      mutationKey: ["confirmPriceCampaign"],
      mutationFn: async ({ campaignId }) => {
        await CampaignsService.confirmPrice({ campaignId });
        return campaignId;
      },
      onSuccess: () => {
        toast.success("Price confirmed successfully!");
        refetchDetails();
      },
    });

  const { mutateAsync: submitCampaign, isPending: submittingCampaign } =
    useMutation<
      any,
      any,
      Parameters<typeof CampaignsService.submitCampaign>[0]
    >({
      mutationKey: ["submitCampaign", campaignId],
      mutationFn: async ({ campaignId }) => {
        await CampaignsService.submitCampaign({ campaignId });
      },
      onSuccess: () => {
        toast.success("Campaign submitted successfully!");
        refetchDetails();
      },
      onError: () => {
        toast.error("Failed to submit campaign. Please try again.");
      },
    });

  const updateQuantitiesJustification = useJustification({
    title: "Update quantities",
    description: "Enter a new quantity for this material request.",
    hideJustificationField: true,
    submitButtonText: "Update",
    additionalContent: (formProps) => {
      return (
        <TextFieldWrapper
          {...formProps}
          label="New quantity"
          fullWidth
          name="quantity"
        />
      );
    },
    additionalData: [
      {
        initialValue: 0,
        key: "quantity",
        validationSchema: yup
          .number()
          .required("This field is required")
          .min(0),
      },
    ],
  });
  console.log("details", details);
  console.log("materials", materials);
  const spendCategory = details?.spendCategoryL3;
  const materialDescription = spendCategory
    ? `${spendCategory.code} - ${spendCategory.name}`
    : "";
  return !details || loadingDetails || loadingRequests ? (
    <LinearProgress />
  ) : (
    <>
      {updateQuantitiesJustification.JSX}
      <Dialog open={adjustModalOpen} onClose={() => setAdjustModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Adjust Request</DialogTitle>

        <DialogContent>
          <Grid container spacing={2} mt={1}>

            <Grid item xs={6}>
              <TextField
                select
                label="Incoterms"
                fullWidth
                value={adjustModalData?.incoTerm?.id ?? 0}

                onChange={(e) => {
                  const value = Number(e.target.value);

                  setAdjustModalData((prev) => ({
                    ...prev,
                    incoTerm: {
                      id: value,
                      name: value === 0 ? "FOB" : "DDP",
                    },
                  }));
                }}
              >
                <MenuItem value={0}>FOB</MenuItem>
                <MenuItem value={1}>DDP</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={6}>
              <TextField
                label="Material Name"
                disabled={isRequestorEditMode}
                fullWidth
                value={materialName}
                onChange={(e) => setMaterialName(e.target.value)}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                label="Quantity"
                type="number"
                fullWidth
                value={quantity}
                onChange={(e) => {
                  const val = e.target.value;
                  if (!/^\d*$/.test(val)) return; // only allow digits
                  setQuantity(val === "" ? 0 : Number(val));
                }}
                inputProps={{
                  step: 1,      // prevents decimals via arrows
                  min: 0,
                }}
              />
            </Grid>

            <Grid item xs={6}>


              <FormControl fullWidth>
                <InputLabel id="uom-label">Quantity (units)</InputLabel>

                <Select
                  disabled={isRequestorEditMode}
                  labelId="uom-label"
                  label="Quantity (units)"
                  value={adjustModalData?.quantityUnits || "EA"}
                  onChange={(e) => {
                    const value = e.target.value;

                    setAdjustModalData((prev) => ({
                      ...prev,
                      quantityUnits: value,
                    }));
                  }}
                >
                  {uomList.map((uom) => (
                    <MenuItem key={uom} value={uom}>
                      {uom}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={6}>
              <TextField
                label="Adjusted Price"
                disabled={isRequestorEditMode}
                type="number"
                fullWidth
                value={adjustedPrice}
                onChange={(e) => {
                  const val = e.target.value;

                  if (val === "" || Number(val) >= 0) {
                    setAdjustedPrice(val === "" ? "" : Number(val));
                  }
                }}
              />
            </Grid>

            <Grid item xs={6}>
              <CustomCurrencySelect
                disabled={isRequestorEditMode}
                value={currency}
                onChange={(value) => setCurrency(value)}
                required={!isRequestorEditMode}
              />

            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="vendor-label">Vendor</InputLabel>
                <Select
                  disabled={isRequestorEditMode}
                  labelId="vendor-label"
                  label="Vendor"
                  value={selectedVendor?.vendorCode || ""}
                  onChange={(e) => {
                    const vendor = vendors.find(
                      (v) => v.vendorCode === e.target.value
                    );
                    setSelectedVendor(vendor || null);
                  }}
                >
                  {vendors.map((vendor) => (
                    <MenuItem
                      key={vendor.vendorCode}
                      value={vendor.vendorCode}
                    >
                      {vendor.vendorCode} - {vendor.vendorName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Comment"
                fullWidth
                multiline
                rows={3}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
              />
            </Grid>

          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setAdjustModalOpen(false)}>Cancel</Button>
          <Button variant="contained"
            onClick={handleAdjustSubmit}
            disabled={
              isRequestorEditMode
                ? adjustModalData?.incoTerm?.id ===
                    initialMarketProcAdjustValues.incoTermId &&
                  quantity === initialMarketProcAdjustValues.quantity &&
                  comment === initialMarketProcAdjustValues.comment
                : quantity <= 0 ||
                  (adjustedPrice !== "" && adjustedPrice < 0) ||
                  !currency
            }

          >
            Update
          </Button>
        </DialogActions>
      </Dialog>
      <Drawer
        open={!!drawerInitialData}
        onClose={() => setDrawerInitialData(null)}
        title="Product details"
        footerContent={
          !!drawerInitialData &&
          canPreOrderCampaign(details.state) && (
            <CampaignMaterialPreOrderButton
              allMaterials={materials}
              material={drawerInitialData}
              campaign={details}
              requests={requests.find(
                (req) => req.orderWindowMaterialId === drawerInitialData?.id
              )}
              onRefetch={() => {
                refetchMaterials();
                refetchRequests();
                refetchActivityLog();
                refetchMaterialActivityLog();
                setDrawerInitialData(null);
                setTab(5);
              }}
            />
          )
        }
      >
        <CampaignMaterialForm
          initialValues={drawerInitialData}
          onClose={() => {
            setDrawerInitialData(null);
            refetchMaterials();
            refetchDetails();
          }}
          onRefetchLogs={refetchMaterialActivityLog}
        />
      </Drawer>
      <Drawer
        title="Edit Campaign"
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
      >
        {createDialogOpen && (
          <CreateCampaignContainer
            onClose={() => {
              setCreateDialogOpen(false);
              refetchMaterials();
              refetchDetails();
            }}
            initialData={details}
            onRefetchLogs={refetchActivityLog}
          />
        )}
      </Drawer>
      <Box
        sx={{
          m: -3,
          mb: 3,
          p: 3,
          background: theme.palette.background.paper,
          position: "relative",
        }}
      >
        <Breadcrumbs
          sx={{ mb: 3 }}
          separator={<NavigateNext fontSize="small" />}
          aria-label="breadcrumb"
        >
          {[
            <NavLink key="1" color="inherit" to="/campaigns" onClick={() => { }}>
              Campaigns
            </NavLink>,
            <Typography key="3" color="text.primary">
              {details ? details.name : `Campaign #${campaignId}`}
            </Typography>,
          ]}
        </Breadcrumbs>

        <Grid
          container
          spacing={4}
          justifyContent={"space-between"}
          alignContent={"center"}
        >

          {/* Place here */}
          <Grid item >
            {details && (
              <>
                {/* OW #470 */}
                <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                  OW #{details?.id}
                </Typography>

                {/* Active */}
                <Typography variant="h6" color="text.secondary">
                  {getStateDisplayLabel(details?.state)}
                </Typography>
              </>
            )}
          </Grid>
          <Grid item flexGrow={1}>
            {details?.name && (
              <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                {details?.name}
              </Typography>
            )}

            <Typography variant="h6">
              <Tooltip
                title={`Order window open from ${moment(
                  details?.startDate
                ).format("DD/MM/YYYY")} until
                ${moment(details?.endDate).format("DD/MM/YYYY")}`}
              >
                <span>
                  {details.state === CampaignState.Draft
                    ? `Modified ${moment(details.modified).fromNow()}`
                    : details.state === CampaignState.Active || isPendingState(details.state)
                      ? `Ending ${moment(details.endDate).fromNow()}`
                      : `Starting ${moment(details.startDate).fromNow()}`}
                </span>
              </Tooltip>
            </Typography>
          </Grid>
          <Grid item>

            {!details.isDeleted && (
              <ButtonGroup size="large" variant="outlined">
                {!details.isDeleted && (
                  <ButtonGroup size="large" variant="outlined">
                    {details.status === 8 &&
                      details.state !== CampaignState.Past &&
                      hasPermission("Campaign.Edit") &&
                      canEditCampaign(details.state) && (
                        <Button
                          disabled={duplicatingCampaign}
                          onClick={() => duplicateCampaign({ campaignId })}
                          startIcon={<Archive />}
                        >
                          Copy
                        </Button>
                      )}
                  </ButtonGroup>
                )}
                {details.status === 8 &&
                  details.state !== CampaignState.Past &&
                  hasPermission("Campaign.Edit") &&
                  canEditCampaign(details.state) && (
                    <Button
                      disabled={confirmingPrice}
                      onClick={() => confirmPriceCampaign({ campaignId })}
                      startIcon={<Archive />}
                    >
                      Confirm Price
                    </Button>
                  )}
                {(
                  details.state !== CampaignState.Past &&
                  (
                    hasPermission("Campaign.Archive") &&
                    canEditCampaign(details.state)
                  )
                ) && (
                    <Button
                      disabled={archivingCampaign}
                      onClick={() => {
                        archiveCampaign({ campaignId });
                      }}
                      startIcon={<Archive />}
                    >
                      Archive
                    </Button>
                  )}

                {(hasPermission("Campaign.Edit") &&
                  canEditCampaign(details.state)) && (
                    <>
                      <Button
                        onClick={() => setCreateDialogOpen(true)}
                        startIcon={<Edit />}
                      >
                        Edit
                      </Button>
                      {hasPermission("Campaign.Publish") &&
                        !isPendingState(details.state) && (
                          <Tooltip
                            arrow
                            title={
                              details.publishErrors?.length > 0
                                ? details.publishErrors[0].message
                                : "Publish campaign"
                            }
                          >
                            <div>
                              <Button
                                disabled={
                                  details.publishErrors?.length > 0 ||
                                  publishingCampaign
                                }
                                startIcon={<Send />}
                                onClick={() => {
                                  publishCampaign();
                                }}
                              >
                                Publish
                              </Button>
                            </div>
                          </Tooltip>
                        )}
                    </>
                  )}
                {details.state === CampaignState.Pending_Market_Proc &&
                  hasPermission("Campaign.Edit") && (
                    <Tooltip arrow title="This will end the campaign.">
                      <Button
                        disabled={closingCampaign}
                        startIcon={<Close />}
                        onClick={() => {
                          closeCampaign();
                        }}
                      >
                        Close Order Window
                      </Button>
                    </Tooltip>
                  )}
                {canSubmitPendingCampaign(details.state) &&
                  hasPermission("Campaign.Submit") && (
                    <Button
                      disabled={submittingCampaign}
                      startIcon={<Send />}
                      onClick={() => {
                        submitCampaign({ campaignId });
                      }}
                    >
                      Submit
                    </Button>
                  )}
                {!subscribers.find(
                  (subscriber) => subscriber.email === currentUser.email
                ) ? (
                  <Tooltip
                    arrow
                    title="Subcribe to this campaign to receive updates"
                  >
                    <Button
                      startIcon={<NotificationAddOutlined />}
                      onClick={() => subcribeMeToCampaign()}
                      disabled={subscribeMePending}
                    >
                      Subscribe
                    </Button>
                  </Tooltip>
                ) : (
                  <Tooltip arrow title="Unsubscribe from this campaign">
                    <Button
                      startIcon={<NotificationsOffRounded />}
                      onClick={() =>
                        unsubscribeMemberFromCampaign(currentUser.email)
                      }
                      disabled={unsubscribePending}
                    >
                      Unsubscribe
                    </Button>
                  </Tooltip>
                )}
              </ButtonGroup>
            )}
          </Grid>

        </Grid>
      </Box >
      <Container maxWidth="xl">
        <Grid container spacing={2} alignItems="stretch">
          <Grid item xs={12}>
          </Grid>
          <Grid item xs={12}>
            <Paper>
              <Tabs
                value={tab}
                onChange={(_, newValue) => setTab(newValue)}
                indicatorColor="primary"
                textColor="primary"
                variant="scrollable"
                sx={{ borderBottom: 1, borderColor: "divider" }}
              >
                <Tab label="Campaign Image" />   {/* ✅ NEW TAB */}
                <Tab label="Campaign Details" />
                <Tab label={`Materials (${materials?.filter((material) => !material.deleted).length})`} />
                <Tab label={`Attachments (${details?.mediaFiles?.length})`} />
                <Tab label={`Subscribers (${subscribers?.length})`} />
                <Tab label="Activity Log" />
                <Tab label="Quantity Consolidation" />
                <Tab label="Tasks" />
              </Tabs>
              {tab === 0 && (
                <Box padding={2}>
                  {images.length > 0 ? (
                    <Paper
                      sx={{
                        overflow: "hidden",
                        position: "relative",
                        borderRadius: "8px",
                      }}
                      elevation={3}
                    >
                      <PUIRatioBox xRatio={16} yRatio={9}>
                        {[...images]
                          .sort((a, b) => new Date(a.uploadedOn).getTime() - new Date(b.uploadedOn).getTime())
                          .map((image, index) => (
                            <Slide
                              key={image.url}
                              isActive={activeSlide === index}
                              backgroundImage={image.url}
                              onNext={
                                activeSlide === images.length - 1
                                  ? null
                                  : () => setActiveSlide(index + 1)
                              }
                              onPrevious={
                                activeSlide === 0
                                  ? null
                                  : () => setActiveSlide(index - 1)
                              }
                            />
                          ))}
                      </PUIRatioBox>
                    </Paper>
                  ) : (
                    <Typography>No campaign image available</Typography>
                  )}
                </Box>
              )}
              {tab === 1 && (
                <Box padding={2}>
                  <Grid container spacing={10}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="h5" sx={{ mb: 4 }}>
                        Campaign Details
                      </Typography>
                      <Grid container spacing={4} alignItems={"center"}>
                        <Grid item xs={4}>
                          Name:
                        </Grid>
                        <Grid item xs={8}>
                          {details.name}
                        </Grid>
                        <Grid item xs={4}>
                          Description:
                        </Grid>
                        <Grid item xs={8}>
                          {details.description}
                        </Grid>
                        <Grid item xs={4}>
                          Buyer Email:
                        </Grid>
                        <Grid item xs={8}>
                          {details.strategicBuyerEmailAddress}
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="subtitle2">
                            Quality Approvers:
                          </Typography>

                          {details?.qualityApprovers?.length ? (
                            <Box display="flex" gap={1} flexWrap="wrap" mt={1}>
                              {details.qualityApprovers.map((email) => (
                                <Chip key={email} label={email} size="small" />
                              ))}
                            </Box>
                          ) : (
                            <Typography variant="body2">-</Typography>
                          )}
                        </Grid>

                        <Grid item xs={4}>
                          Agreements:
                        </Grid>
                        <Grid item xs={8}>
                          {details.agreements}
                        </Grid>
                        <Grid item xs={4}>
                          Incoterms:
                        </Grid>
                        <Grid item xs={8}>
                          {details.incoTerms}
                        </Grid>
                        <Grid item xs={4}>
                          Locations:
                        </Grid>
                        <Grid item xs={8}>
                          {[
                            ...details.zones?.map((z) => z.name),
                            ...details.markets?.map((m) => m.name),
                            ...details.companyCodes?.map((c) => c.name),
                          ].join(", ")}
                        </Grid>

                        <Grid item xs={4}>
                          Start Date:
                        </Grid>
                        <Grid item xs={8}>
                          {moment(details.startDate).format("DD/MM/YYYY")}
                        </Grid>
                        <Grid item xs={4}>
                          End Date:
                        </Grid>
                        <Grid item xs={8}>
                          {moment(details.endDate).format("DD/MM/YYYY")}
                        </Grid>
                        <Grid item xs={4}>
                          Prices are valid until:
                        </Grid>
                        <Grid item xs={8}>
                          {moment(details.validTo).format("DD/MM/YYYY")}
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="h5" sx={{ mb: 4 }}>
                        Supplier Details
                      </Typography>
                      <Grid container spacing={4} alignItems={"center"}>
                        <Grid item xs={4}>
                          Parent Vendor:
                        </Grid>
                        <Grid item xs={8}>
                          [{details.supplierCode}] - {details.supplierName}
                        </Grid>
                        <Grid item xs={4}>
                          Contact:
                        </Grid>
                        <Grid item xs={8}>
                          <PUIUser
                            name={details.supplierContact.username}
                            email={details.supplierContact.email}
                          />
                        </Grid>
                      </Grid>
                      {(details.state === CampaignState.Past || isPendingState(details.state)) &&
                        !details.isDeleted && (
                          <Grid container spacing={4}>
                            <Grid item xs={12} sx={{ my: 6 }}>
                              <Button
                                fullWidth
                                size="large"
                                color="primary"
                                variant="contained"
                                startIcon={<Link />}
                                component={NavLink}
                                disabled={!details.parentPromoItemId}
                                to={`/catalogs/${details.parentPromoItemId}/view`}
                              >
                                {!details.parentPromoItemId ? (
                                  "No Parent is available for this Campaign"
                                ) : (
                                  <>
                                    Parent Promo ID: {details.parentPromoItemId}
                                  </>
                                )}
                              </Button>
                            </Grid>
                          </Grid>
                        )}
                    </Grid>
                  </Grid>
                </Box>
              )}

              {tab === 2 && (
                <Box padding={4}>
                  <Grid container spacing={4}>
                    {materials
                      ?.filter((material) => !material.deleted)
                      .map((material) => {
                        return (
                          <Grid
                            item
                            xs={12}
                            sm={6}
                            md={4}
                            lg={3}
                            key={material.id}
                          >

                            <CampaignMaterialCard
                              material={material}
                              campaign={details}

                              canUpdate={canUpdateMaterial}

                              canDuplicate={
                                canDuplicateMaterial &&
                                canCreateOrDuplicateMaterial(details.state)
                              }

                              canDelete={canDeleteMaterial}

                              requests={requests.find(
                                (req) =>
                                  req.orderWindowMaterialId === material.id
                              )}

                              onRemove={(material) => {
                                removeCampaignMaterial(material.id);
                              }}

                              onDuplicate={(material) => {
                                duplicateCampaignMaterial(material.id);
                              }}

                              onEdit={(material) => {
                                setDrawerInitialData(material);
                              }}

                              onDetails={(material) => {
                                setDrawerInitialData({
                                  ...material,
                                  readOnly: true,
                                  allowOrder: true,
                                });
                              }}

                              onRefetch={() => {
                                refetchMaterials();
                                refetchRequests();
                                refetchActivityLog();
                                refetchMaterialActivityLog();
                              }}
                            />
                          </Grid>
                        );
                      })}
                    <Grid item xs={12} display="flex" justifyContent={"center"}>

                      {!details.isDeleted &&
                        canCreateOrDuplicateMaterial(details.state) &&
                        canCreateMaterial && (

                          <Fab
                            color="primary"
                            variant="extended"

                            onClick={() => {
                              console.log("promoCategories", promoCategories);
                              console.log("promoCategoryId", Object.keys(promoCategories)[0]);
                              const spendCategoryCode = spendCategory?.code || "";

                              const defaultMaterial = defaultMaterials?.[0];

                              const defaultPromoCategory: any =
                                Object.values(promoCategories)[0];

                              console.log("defaultPromoCategory", defaultPromoCategory);

                              setDrawerInitialData({
                                orderWindowId: parseInt(campaignId),

                                promoCategoryId: String(defaultPromoCategory?.id || ""),

                                materialNumber:
                                  defaultMaterial?.id || spendCategoryCode,

                                materialName:
                                  defaultMaterial?.name || spendCategory?.name || "",

                                itemDescription:
                                  defaultMaterial?.name || materialDescription,

                                shortDescription:
                                  defaultMaterial?.name || materialDescription,

                                minimumOrderQuantity: 0,
                                minimumPurchaseQuantity: 0,

                                priceRanges: [
                                  {
                                    pricePer: 1,
                                    threshold: 1,
                                    unitPrice: null,
                                  },
                                ],

                                mediaFiles: [],

                                currencyCode:
                                  defaultMaterial?.currencyCode || "USD",

                                unitOfMeasure: "EA",
                                uoM: defaultMaterial?.uoM || "EA",
                              });
                            }}
                          >
                            Add New Material
                          </Fab>
                        )}
                    </Grid>
                  </Grid>
                </Box>
              )}

              {tab === 3 && (
                <Box padding={2}>
                  <Attachments
                    // enableDelete={
                    //   !details.isDeleted &&
                    //   details.state !== CampaignState.Past &&
                    //   [
                    //     IUserRole.PPA_ABOVE_MARKET,
                    //     IUserRole.ABOVE_MARKET,
                    //   ].indexOf(roleId) > -1
                    // }
                    // enableUpload={
                    //   !details.isDeleted &&
                    //   details.state !== CampaignState.Past &&
                    //   [
                    //     IUserRole.PPA_ABOVE_MARKET,
                    //     IUserRole.ABOVE_MARKET,
                    //   ].indexOf(roleId) > -1
                    // }
                    enableDelete={
                      !details.isDeleted &&
                      details.state !== CampaignState.Past &&
                      hasPermission("Campaign.Edit")
                    }

                    enableUpload={
                      !details.isDeleted &&
                      details.state !== CampaignState.Past &&
                      hasPermission("Campaign.Edit")
                    }
                    onClick={(attachment) => {
                      createBlobService(attachment.url, "GET")().then(
                        (blob) => {
                          const url = URL.createObjectURL(blob);
                          const downloadLink = document.createElement("a");
                          downloadLink.href = url;
                          downloadLink.download = attachment.fileName;
                          document.body.appendChild(downloadLink);
                          downloadLink.click();
                          window.URL.revokeObjectURL(url);
                          document.body.removeChild(downloadLink);
                        }
                      );
                    }}
                    onAttach={(file) => {
                      uploadCampaignMedia({
                        campaignId: campaignId,
                        file: file,
                      });
                    }}
                    onDelete={(file) => {
                      deleteCampaignMedia({ fileId: file.id.toString() });
                    }}
                    accept={{
                      "message/rfc822": [".eml"],
                      "application/vnd.ms-outlook": [".msg"],
                      "application/pdf": [".pdf"],
                      "application/msword": [".doc", ".docx"],
                      "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
                        [".docx"],
                      "application/vnd.ms-excel": [".xls", ".xlsx"],
                      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
                        [".xlsx"],
                      "application/vnd.ms-powerpoint": [".ppt", ".pptx"],
                      "application/vnd.openxmlformats-officedocument.presentationml.presentation":
                        [".pptx"],
                      "video/mp4": [".mp4"],
                      "image/svg+xml": [".svg"],
                      "image/png": [".png"],
                      "image/jpeg": [".jpg", ".jpeg"],
                    }}
                    attachments={details?.mediaFiles?.map((mediaFile) => ({
                      creationDate: mediaFile.uploadedOn,
                      fileName: mediaFile.originalFileName,
                      id: mediaFile.fileId,
                      uploadedByUserEmail: mediaFile.uploadedBy?.email,
                      url: mediaFile.url,
                    }))}
                  />
                </Box>
              )}
              {tab === 4 && (
                <SubscribersContainer
                  readOnly={
                    unsubscribePending || details.state !== CampaignState.Active
                  }
                  subscribers={subscribers}
                  onUnsubscribe={(email) =>
                    unsubscribeMemberFromCampaign(email)
                  }
                />
              )}
              {tab === 5 && (
                <CampaignActivityLog entries={combinedActivityLogs} />
              )}
              {tab === 6 && (
                <Box padding={2}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <MaterialReactTable
                        initialState={{
                          columnPinning: {
                            left: ["mrt-row-expand", "mrt-row-select"],
                            right: ["mrt-row-actions"],
                          },
                        }}
                        muiTablePaperProps={({ table }) => ({
                          style: { background: "white !important" },
                          position: "relative",
                          zIndex: table.getState().isFullScreen
                            ? 1300
                            : undefined,
                        })}
                        renderTopToolbarCustomActions={() => (
                          <Button
                            onClick={() => exportQuantityConsolidation()}
                            startIcon={<Download />}
                            variant="outlined"
                          >
                            Export
                          </Button>
                        )}
                        data={requests?.reduce((acc, { requests, ...rest }) => {
                          acc.push(
                            ...requests.map((request) => {
                              return {
                                material: rest,
                                ...request,
                              };
                            })
                          );
                          return acc;
                        }, [])}
                        enableGrouping
                        enableRowActions

                        renderRowActionMenuItems={({ closeMenu, row }) => {
                          const role = currentUser.getUserRole();

                          const isViewOnly = role === IUserRole.VIEW_ONLY;
                          const isSupplier = role === IUserRole.SUPPLIER;

                          // Logged-in user must be the creator of the request
                          const isOwnRequest =
                            currentUser.email?.toLowerCase() ===
                            row.original.requestor?.toLowerCase();

                          const isPendingConfirmPrice =
                            details.state === CampaignState.Pending_Confirm_Price;

                          const isPendingMarketProc =
                            details.state === CampaignState.Pending_Market_Proc;

                          const menuItems = [];

                          // =====================================================
                          // Pending - Confirm Price
                          // Only Supplier can Adjust all rows
                          // =====================================================
                          if (isPendingConfirmPrice && isSupplier) {
                            menuItems.push(
                              <MenuItem
                                key="adjust-confirm-price"
                                onClick={async () => {
                                  const rowData = row.original as any;

                                  setIsRequestorEditMode(false);
                                  setMaterialName(
                                    rowData.materialName || ""
                                  );

                                  setAdjustModalData({
                                    ...rowData,
                                    incoTerm: rowData.incoTerm || {
                                      id: 0,
                                      name: "FOB",
                                    },
                                    quantityUnits: rowData.quantityUnits || "EA",
                                  });

                                  setQuantity(rowData.quantity ?? 0);
                                  // setAdjustedPrice(
                                  //   rowData.adjustedPrice == null
                                  //     ? "-"
                                  //     : rowData.adjustedPrice
                                  // );
                                  setAdjustedPrice(
                                    rowData.adjustedPrice == null
                                      ? ""
                                      : rowData.adjustedPrice
                                  );
                                  setComment(rowData.comments ?? "");
                                  // setCurrency(
                                  //   rowData.currencyCode ||
                                  //   rowData.material?.material?.currencyCode ||
                                  //   "USD"
                                  // );
                                  setCurrency(rowData.adjustedCurrencyCode || "");

                                  // Load vendors
                                  setVendors([]);
                                  setSelectedVendor(null);

                                  const marketId = rowData.market?.id;

                                  if (marketId) {
                                    try {
                                      const vendorList =
                                        await CampaignsService.getVendorsByOrderWindowAndMarket({
                                          orderWindowId: Number(campaignId),
                                          marketId,
                                        });
                                      const normalizedVendors =
                                        normalizeVendorList(vendorList);
                                      const selectedRowVendor =
                                        getSelectedVendorForRow(
                                          normalizedVendors,
                                          rowData
                                        );

                                      setVendors(
                                        getVendorsWithSelectedVendor(
                                          normalizedVendors,
                                          selectedRowVendor
                                        )
                                      );
                                      setSelectedVendor(selectedRowVendor);
                                    } catch (error) {
                                      console.error("Failed to load vendors", error);
                                      setVendors([]);
                                      setSelectedVendor(null);
                                    }
                                  }

                                  setAdjustModalOpen(true);
                                  closeMenu();
                                }}
                              >
                                Adjust
                              </MenuItem>
                            );
                          }

                          // =====================================================
                          // Pending - Market Proc
                          // All roles EXCEPT VIEW_ONLY and SUPPLIER
                          // can Adjust and Revoke ONLY their own requests
                          // =====================================================
                          const canManageOwnRequestInMarketProc =
                            isPendingMarketProc &&
                            !isViewOnly &&
                            !isSupplier &&
                            isOwnRequest;

                          if (canManageOwnRequestInMarketProc) {
                            // Adjust
                            menuItems.push(
                              <MenuItem
                                key="adjust-market-proc"
                                onClick={async () => {
                                  const rowData = row.original as any;

                                  setIsRequestorEditMode(true);
                                  setMaterialName(
                                    rowData.materialName || ""
                                  );

                                  setAdjustModalData({
                                    ...rowData,
                                    incoTerm: rowData.incoTerm || {
                                      id: 0,
                                      name: "FOB",
                                    },
                                    quantityUnits: rowData.quantityUnits || "EA",
                                  });

                                  setQuantity(rowData.quantity ?? 0);
                                  setInitialMarketProcAdjustValues({
                                    incoTermId: rowData.incoTerm?.id ?? 0,
                                    quantity: rowData.quantity ?? 0,
                                    comment: rowData.comments ?? "",
                                  });
                                  // setAdjustedPrice(
                                  //   rowData.adjustedPrice ??
                                  //   rowData.material?.finalPrice ??
                                  //   0
                                  // );
                                  setAdjustedPrice(
                                    rowData.adjustedPrice == null
                                      ? 0
                                      : rowData.adjustedPrice
                                  );
                                  setComment(rowData.comments ?? "");
                                  // setCurrency(
                                  //   rowData.currencyCode ||
                                  //   rowData.material?.material?.currencyCode ||
                                  //   "USD"
                                  // );
                                  setCurrency(rowData.adjustedCurrencyCode || "");

                                  // Load vendors
                                  setVendors([]);
                                  setSelectedVendor(null);

                                  const marketId = rowData.market?.id;

                                  if (marketId) {
                                    try {
                                      const vendorList =
                                        await CampaignsService.getVendorsByOrderWindowAndMarket({
                                          orderWindowId: Number(campaignId),
                                          marketId,
                                        });
                                      const normalizedVendors =
                                        normalizeVendorList(vendorList);
                                      const selectedRowVendor =
                                        getSelectedVendorForRow(
                                          normalizedVendors,
                                          rowData
                                        );

                                      setVendors(
                                        getVendorsWithSelectedVendor(
                                          normalizedVendors,
                                          selectedRowVendor
                                        )
                                      );
                                      setSelectedVendor(selectedRowVendor);
                                    } catch (error) {
                                      console.error("Failed to load vendors", error);
                                      setVendors([]);
                                      setSelectedVendor(null);
                                    }
                                  }

                                  setAdjustModalOpen(true);
                                  closeMenu();
                                }}
                              >
                                Adjust
                              </MenuItem>
                            );

                            // Revoke
                            menuItems.push(
                              <MenuItem
                                key={`revoke-${row.original.id}`}
                                onClick={() => {
                                  deleteQuantityRequest({
                                    id: row.original.id,
                                  });
                                  closeMenu();
                                }}
                              >
                                Revoke
                              </MenuItem>
                            );
                          }

                          return menuItems;
                        }}
                        columns={[
                          { header: "Request #", accessorKey: "id" },
                          {
                            header: "Market",
                            accessorKey: "market.name",
                          },
                          {
                            header: "Incoterm",
                            accessorKey: "incoTerm.name",
                          },
                          {
                            header: "Material Id",
                            accessorKey: "material.material.id",
                          },
                          {
                            header: "Material Name",
                            accessorKey: "materialName",
                          },
                          {
                            header: "Requestor",
                            accessorKey: "requestor",
                          },
                          {
                            header: "Quantity",
                            accessorKey: "quantity",
                          },
                          {
                            header: "Quantity (units)",
                            accessorKey: "quantityUnits",
                          },

                          {
                            header: "Calculated Price",
                            accessorKey: "calculatedPrice",
                            Cell: ({ row }) => {
                              const rowData = row.original as any;

                              const calculatedPrice = rowData.calculatedPrice;
                              const currency =
                                rowData.currencyCode ||
                                rowData.material?.material?.currencyCode ||
                                "-";

                              return calculatedPrice != null
                                ? `${calculatedPrice} ${currency}`
                                : "-";
                            },
                          },
                          {
                            header: "Adjusted Price",
                            accessorKey: "adjustedPrice",
                            Cell: ({ row }) => {
                              const { adjustedPrice, adjustedCurrencyCode } = row.original as any;

                              if (adjustedPrice == null) return "-";

                              if (
                                adjustedCurrencyCode == null ||
                                adjustedCurrencyCode === ""
                              ) {
                                return adjustedPrice;
                              }

                              return `${adjustedPrice} ${adjustedCurrencyCode}`;
                            },
                          },
                          {
                            header: "Minimum Order Quantity",
                            accessorKey: "material.minimumOrderQuantity",
                          },
                          // {
                          //   header: "MOQ met",
                          //   accessorKey: "material.moqMet",
                          //   Cell: ({ row }) => {
                          //     return row.original.moqMet
                          //       //row.original.material.moqMet
                          //       ? "Yes"
                          //       : "No";
                          //   },
                          // },
                          {
                            header: "MOQ met",
                            accessorKey: "moqMet",
                            Cell: ({ row }) => {
                              return row.original.moqMet ? "Yes" : "No";
                            },
                          },
                          {
                            header: "Vendor",
                            accessorKey: "vendorCode",
                            Cell: ({ row }) => {
                              const rowData = row.original as any;

                              const vendorCode = rowData.vendorCode;
                              const vendorName = rowData.vendorName;

                              // If both values are empty/null, show "-"
                              if (!vendorCode && !vendorName) {
                                return "-";
                              }

                              // If both values exist, show vendorCode-vendorName
                              if (vendorCode && vendorName) {
                                return `${vendorCode}-${vendorName}`;
                              }

                              // If only one exists, show that value
                              return vendorCode || vendorName || "-";
                            },
                          },
                          {
                            header: "Quality Approved",
                            accessorKey: "qualityApproved",
                            Cell: ({ row }) => {
                              return row.original.qualityApproved ? "Yes" : "No";
                            },
                          },

                          {
                            header: "Comments",
                            accessorKey: "comments",
                          },
                          {
                            header: "Status",
                            accessorKey: "status",
                            Cell: ({ row }) => {
                              return row.original.status || "-";
                            },
                          },
                          {
                            header: "Easy Request Number",
                            accessorKey: "easyRequestNumber",
                            Cell: ({ row }) => {
                              return row.original.easyRequestNumber || "-";
                            },
                          },
                          {
                            header: "Attachment",
                            accessorKey: "mediaFiles",
                            Cell: ({ row }) => {
                              return row.original.mediaFiles?.length > 0 ? (
                                <Link
                                  href={row.original.mediaFiles[0].url}
                                  underline="hover"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    createBlobService(
                                      row.original.mediaFiles[0].url,
                                      "GET"
                                    )().then((blob) => {
                                      const url = URL.createObjectURL(blob);
                                      const downloadLink =
                                        document.createElement("a");
                                      downloadLink.href = url;
                                      downloadLink.download =
                                        row.original.mediaFiles[0].originalFileName;
                                      document.body.appendChild(downloadLink);
                                      downloadLink.click();
                                      window.URL.revokeObjectURL(url);
                                      document.body.removeChild(downloadLink);
                                    });
                                  }}
                                >
                                  {row.original.mediaFiles[0]
                                    .originalFileName || "Download"}
                                </Link>
                              ) : (
                                "No"
                              );
                            },
                          },

                        ]}
                      />
                    </Grid>
                  </Grid>
                </Box>
              )}
              {tab === 7 && (
                <Box padding={2}>
                  {isError && (
                    <Typography color="error" sx={{ mb: 2 }}>
                      Failed to load Tasks data.
                    </Typography>
                  )}
                  <MaterialReactTable

                    columns={[
                      {
                        header: "Task ID",
                        Cell: ({ row }: any) => {
                          const id = row.original.pslId;

                          return (
                            <a href={`/catalogs/${id}/view`} target="_blank">
                              {row.original.pslIdWithPrefix}
                            </a>
                          );
                        },
                      },
                      {
                        header: "Status",
                        Cell: ({ row }: any) => {
                          const status = row.original.pslStatus;

                          const statusMap: Record<number, string> = {
                            0: "Pending",
                            1: "Approved",
                            2: "Rejected",
                            3: "Pending to Accept Changes",
                            4: "Pending Quality Pre Approval",
                            5: "Quality Rejected",
                          };

                          return statusMap[status] || "-";
                        },
                      },
                      {
                        header: "Scope",
                        Cell: ({ row }: any) =>
                          row.original.scope === 1 ? "Above Market" : "At Market",
                      },

                      {
                        header: "Market",
                        Cell: ({ row }: any) => {
                          const markets = row.original.markets;

                          if (!markets || markets.length === 0) {
                            return "-";
                          }

                          return markets.map((m: any) => m.name).join(", ");
                        },
                      },
                      {
                        header: "Parent Vendor",
                        Cell: ({ row }: any) => {
                          const r = row.original;

                          const code =
                            r.scope === 1 ? r.childSupplierCode : r.parentCode;

                          const name =
                            r.scope === 1 ? r.childSupplierName : r.parentName;

                          if (code && name) return `${code} - ${name}`;
                          if (code) return code;
                          if (name) return name;

                          return "-";
                        },
                      },
                      // {
                      //   header: "Child Vendor",
                      //   accessorKey: "supplierName",
                      // },

                      {
                        header: "Child Vendor",
                        Cell: ({ row }: any) => {
                          const code = row.original.childSupplierCode;
                          const name = row.original.childSupplierName;

                          if (code && name) return `${code} - ${name}`;
                          if (code) return code;
                          if (name) return name;

                          return "-";
                        },
                      },
                      {
                        header: "Approvers",
                        Cell: ({ row }: any) => {
                          const r = row.original;
                          const isParent = r.scope === 1;

                          let approvers: string[] = [];

                          if (isParent) {
                            if (
                              ["Pending Approval", "Rejected", "Mandatory"].includes(r.statusName)
                            ) {
                              approvers = r.approvers || [];
                            } else if (r.statusName === "Pending Quality Pre Approval") {
                              return r.approvedBy || "-";
                            }
                          } else {
                            if (!r.localizer) {
                              approvers = r.approvers || [];
                            } else {
                              approvers = r.localizer || [];
                            }
                          }

                          // Remove empty values
                          approvers = approvers.filter((a) => a?.trim());

                          if (approvers.length === 0) return "-";

                          if (approvers.length === 1) {
                            return approvers[0];
                          }

                          return (
                            <Tooltip
                              title={
                                <Box>
                                  {approvers.map((email, index) => (
                                    <Typography key={index} variant="body2">
                                      {email}
                                    </Typography>
                                  ))}
                                </Box>
                              }
                              arrow
                              placement="top"
                            >
                              <span style={{ cursor: "pointer" }}>
                                {approvers.length} Approvers
                              </span>
                            </Tooltip>
                          );
                        },
                      },
                      {
                        header: "Quality Approvers",
                        Cell: ({ row }: any) => {
                          const r = row.original;
                          const isParent = r.scope === 1;

                          const normalize = (data: any): string[] => {
                            if (!data) return [];
                            if (typeof data === "string") return data.trim() ? [data] : [];
                            if (Array.isArray(data)) return data.filter((a) => a?.trim());
                            return [];
                          };

                          let qualityApprovers: string[] = [];

                          if (isParent) {
                            qualityApprovers = normalize(r.qualityApprovers);
                          } else {
                            const parent = row?.table?.options?.data?.find(
                              (p: any) => p.pslId === r.parentId
                            );

                            qualityApprovers =
                              normalize(r.qualityApprovers).length > 0
                                ? normalize(r.qualityApprovers)
                                : normalize(parent?.qualityApprovers);
                          }


                          if (qualityApprovers.length === 0) return "-";

                          if (qualityApprovers.length === 1) return qualityApprovers[0];

                          return (
                            <Tooltip title={qualityApprovers.join(", ")} arrow>
                              <span style={{ cursor: "pointer" }}>
                                {qualityApprovers.length} Approvers
                              </span>
                            </Tooltip>
                          );
                        },
                      }
                    ]}
                    data={tasksData?.data || []}
                    state={{ isLoading: tasksLoading }}
                  />
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Container >
    </>
  );
};

export default CampaignDetailsContainer;
